/**
 * Shared display helpers for the feedback event screens.
 *
 * In their own module rather than beside a component, because a file that
 * exports both a component and a constant breaks fast refresh — the tab would
 * remount on every edit instead of hot-reloading.
 */
import type { FeedbackSubmission } from '../../types/feedback-submission.types';

/** Colour a submission's status by what it asks of the reader. */
export const statusSeverity = (s?: string) => {
  switch ((s || '').toUpperCase()) {
    case 'NEW': return 'info';
    case 'IN_REVIEW': return 'warning';
    case 'REVIEWED': return 'success';
    case 'ACTION_REQUIRED': return 'danger';
    default: return 'secondary';   // CLOSED
  }
};

/** A rating means nothing without its scale — 3/5 and 3/10 are different answers. */
export const ratingLabel = (d: FeedbackSubmission): string | null => {
  const r = d.derivedSignals?.rating;
  if (r === null || r === undefined) return null;
  return `${r}/${d.derivedSignals?.ratingScale ?? 5}`;
};

/** POSITIVE reads green, NEGATIVE red, anything else neutral. */
export const sentimentSeverity = (s?: string | null) => {
  const v = (s || '').toUpperCase();
  if (v === 'POSITIVE') return 'success';
  if (v === 'NEGATIVE') return 'danger';
  return 'secondary';
};

/**
 * Sentiment as a symbol rather than a word.
 *
 * Keyed by CODE and deliberately incomplete-tolerant: `feedbackSentiment` is a
 * value set, so a code added through the Value Sets screen must not break the
 * form. Anything missing here falls back to its own label, which is readable if
 * less pretty — the alternative, an empty button, is not.
 *
 * Two codes by design — thumb up or thumb down. Someone with nothing to say
 * leaves the question unanswered rather than picking a middle option.
 */
export const SENTIMENT_ICON: Record<string, string> = {
  POSITIVE: 'pi-thumbs-up-fill',
  NEGATIVE: 'pi-thumbs-down-fill',
};

/** Selected-state styling per code, paired with SENTIMENT_ICON. */
export const SENTIMENT_ON: Record<string, string> = {
  POSITIVE: 'border-emerald-500 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30',
  NEGATIVE: 'border-rose-500 bg-rose-50 text-rose-600 dark:bg-rose-900/30',
};

/** Read-only stars, for grids and the submission detail. */
export const starText = (rating?: number | null, scale?: number | null): string | null => {
  if (rating === null || rating === undefined) return null;
  const max = scale ?? 5;
  return '★'.repeat(Math.max(0, Math.min(rating, max)))
    + '☆'.repeat(Math.max(0, max - rating));
};
